import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, GraduationCap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const links = [
  { label: 'Home', href: '#hero' },
  { label: 'About', href: '#about' },
  { label: 'Menu', href: '#menu' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Reviews', href: '#reviews' },
  { label: 'Location', href: '#location' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Contact', href: '#contact' },
];

function scrollTo(href) {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function Navbar({ onStudentOffer }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('hero');
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin: '-40% 0px -55% 0px' }
    );
    document.querySelectorAll('section[id]').forEach(s => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleLink = (e, href) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => scrollTo(href), 50);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-navy-dark shadow-[0_2px_30px_rgba(0,0,0,0.5)]'
          : 'bg-gradient-to-b from-navy-dark/90 via-navy-dark/60 to-transparent'
      }`}
    >
      <nav
        className="max-w-7xl mx-auto px-4 sm:px-6 h-[88px] flex items-center justify-between gap-4"
        aria-label="Main navigation"
      >
        {/* ── Logo ── */}
        <a
          href="#hero"
          onClick={e => handleLink(e, '#hero')}
          className="flex-shrink-0 flex items-center group"
          aria-label="Himalayan Bowl & Juice Bar — Home"
        >
          <img
            src="/logo.png"
            alt="Himalayan Bowl & Juice Bar logo"
            className={`transition-all duration-500 drop-shadow-[0_0_8px_rgba(245,166,35,0.5)]
              ${scrolled ? 'h-16' : 'h-20'}
              group-hover:scale-105`}
          />
        </a>

        {/* ── Desktop links ── */}
        <ul className="hidden md:flex items-center gap-0.5 ml-auto mr-4">
          {links.map(l => (
            <li key={l.href}>
              <a
                href={l.href}
                onClick={e => handleLink(e, l.href)}
                className={`relative px-3 py-2 text-[11px] font-bold tracking-[0.14em] uppercase transition-colors duration-200
                  ${active === l.href.slice(1)
                    ? 'text-brand-orange-light'
                    : 'text-white/80 hover:text-brand-orange-light'}`}
              >
                {l.label}
                {active === l.href.slice(1) && (
                  <motion.span
                    layoutId="nav-underline"
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-brand-orange rounded-full"
                  />
                )}
              </a>
            </li>
          ))}
        </ul>

        <button
          onClick={onStudentOffer}
          className="hidden md:inline-flex items-center gap-1.5 py-2 px-4 rounded-full text-[11px] font-bold tracking-[0.12em] uppercase transition-all duration-200 hover:opacity-90"
          style={{
            background: 'linear-gradient(135deg, rgba(245,166,35,0.18), rgba(245,166,35,0.08))',
            border: '1.5px solid rgba(245,166,35,0.5)',
            color: '#F9C76B',
          }}
        >
          <GraduationCap className="w-3.5 h-3.5" />
          Student Offer
        </button>

        <button
          onClick={() => navigate('/menu')}
          className="hidden md:inline-flex btn-orange !py-2 !px-5 !text-[11px]"
        >
          🍽 Full Menu
        </button>

        {/* ── Burger ── */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 border border-white/15 text-white hover:bg-brand-orange/20 hover:border-brand-orange/40 transition-colors"
          onClick={() => setOpen(o => !o)}
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </nav>

      {/* ── Mobile drawer ── */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/65 z-40 md:hidden"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed top-0 right-0 h-full w-72 bg-navy-dark z-50 md:hidden flex flex-col overflow-y-auto"
            >
              {/* Drawer logo */}
              <div className="flex items-center justify-between px-6 pt-5 pb-6 border-b border-white/10">
                <img src="/logo.png" alt="Himalayan Bowl & Juice Bar" className="h-16 drop-shadow-[0_0_8px_rgba(245,166,35,0.5)]" />
                <button
                  onClick={() => setOpen(false)}
                  className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-brand-orange/30 transition-colors"
                  aria-label="Close menu"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <nav className="flex flex-col px-4 py-4 gap-1 flex-1">
                {links.map((l, i) => (
                  <motion.a
                    key={l.href}
                    href={l.href}
                    onClick={e => handleLink(e, l.href)}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold tracking-[0.1em] uppercase transition-all
                      ${active === l.href.slice(1)
                        ? 'bg-brand-orange/15 text-brand-orange-light border border-brand-orange/25'
                        : 'text-white/75 hover:bg-white/5 hover:text-brand-orange-light'}`}
                  >
                    {l.label}
                  </motion.a>
                ))}
              </nav>

              <div className="px-6 pb-8 flex flex-col gap-3">
                <button
                  onClick={() => { setOpen(false); onStudentOffer?.(); }}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-[12px] font-bold tracking-widest uppercase transition-all hover:opacity-90"
                  style={{
                    background: 'linear-gradient(135deg, rgba(245,166,35,0.18), rgba(245,166,35,0.08))',
                    border: '1.5px solid rgba(245,166,35,0.5)',
                    color: '#F9C76B',
                  }}
                >
                  <GraduationCap className="w-4 h-4" />
                  Student Offer
                </button>
                <button onClick={() => { setOpen(false); navigate('/menu'); }} className="btn-orange w-full justify-center">
                  🍽 Full Menu
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
